import { useEffect, useState } from "react";

function RolFormModal({ isOpen, onClose, rolData, onSubmit }) {
  const isEdit = !!rolData;

  const [form, setForm] = useState({ nombre: "" });

  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  const getNiceErrorMessage = (error, fallback = "Could not save role.") => {
    const msg =
      error?.response?.data?.message ||
      error?.message ||
      fallback;

    const clean = String(msg).replace(/\s+/g, " ").trim();
    return clean.length > 220 ? clean.slice(0, 220) + "…" : clean;
  };

  const showAlert = (type, message, ms = 3500) => {
    setAlert({ type, message });
    window.clearTimeout(showAlert._t);
    showAlert._t = window.setTimeout(() => setAlert(null), ms);
  };

  useEffect(() => {
    if (!isOpen) return;

    setForm({ nombre: rolData?.nombre || "" });
    setAlert(null);
    setSaving(false);
  }, [isOpen, rolData]);

  useEffect(() => {
    if (!isOpen) return;

    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const updateField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setAlert(null);

    const nombre = String(form.nombre || "").trim();
    if (!nombre) {
      showAlert("error", "Role name is required.");
      return;
    }

    setSaving(true);
    try {
      await onSubmit({ nombre });
      showAlert("success", isEdit ? "Role updated successfully." : "Role created successfully.");
      window.setTimeout(() => onClose?.(), 650);
    } catch (error) {
      console.error("Role save failed:", error);
      showAlert("error", getNiceErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={ui.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div style={ui.modal}>
        <div style={ui.header}>
          <h2 style={ui.title}>{isEdit ? "Edit Role" : "Create Role"}</h2>
          <button style={ui.closeBtn} onClick={onClose} title="Close">✕</button>
        </div>

        {alert && (
          <div
            style={{
              ...ui.alert,
              ...(alert.type === "error"
                ? ui.alertError
                : alert.type === "success"
                ? ui.alertSuccess
                : ui.alertInfo),
            }}
          >
            <span style={{ lineHeight: 1.3 }}>{alert.message}</span>
            <button style={ui.alertX} onClick={() => setAlert(null)}>✕</button>
          </div>
        )}

        <form onSubmit={submit} style={ui.formGrid}>
          <div style={ui.formGroup}>
            <label style={ui.label}>Role name</label>
            <input
              name="nombre"
              value={form.nombre}
              onChange={updateField}
              required
              style={ui.input}
              placeholder="Enter role name"
              disabled={saving}
              autoFocus
            />
          </div>

          <div style={ui.buttonRow}>
            <button type="submit" style={{ ...ui.btnPrimary, opacity: saving ? 0.7 : 1 }} disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Role"}
            </button>

            <button type="button" onClick={onClose} style={ui.btnCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ui = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    padding: "18px",
  },

  modal: {
    width: "440px",
    background: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
    border: "1px solid #E2E8F0",
    fontFamily: "Segoe UI, sans-serif",
    overflow: "hidden",
  },

  header: {
    padding: "18px 22px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
  },

  title: {
    fontSize: "20px",
    fontWeight: 800,
    margin: 0,
    color: "#2F4779",
  },

  closeBtn: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "20px",
    color: "#64748b",
    padding: "6px 8px",
    borderRadius: "8px",
    lineHeight: 1,
  },

  alert: {
    margin: "14px 22px 0",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    fontSize: "13px",
    fontWeight: 700,
  },
  alertError: {
    background: "#FEF2F2",
    borderColor: "#FECACA",
    color: "#7F1D1D",
  },
  alertSuccess: {
    background: "#ECFDF5",
    borderColor: "#A7F3D0",
    color: "#065F46",
  },
  alertInfo: {
    background: "#EFF6FF",
    borderColor: "#BFDBFE",
    color: "#1E3A8A",
  },
  alertX: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "16px",
    opacity: 0.75,
  },

  formGrid: {
    display: "grid",
    gap: "16px",
    padding: "18px 22px 22px",
  },

  formGroup: {
    display: "flex",
    flexDirection: "column",
  },

  label: {
    marginBottom: "6px",
    fontSize: "14px",
    color: "#475569",
    fontWeight: 700,
  },

  input: {
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #CBD5E1",
    background: "#F8FAFC",
    color: "#1E293B",
    fontSize: "14px",
    outline: "none",
  },

  buttonRow: {
    marginTop: "6px",
    display: "flex",
    gap: "12px",
  },

  btnPrimary: {
    flex: 1,
    padding: "12px",
    background: "#3B82F6",
    border: "none",
    borderRadius: "10px",
    color: "white",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 800,
  },

  btnCancel: {
    flex: 1,
    padding: "12px",
    background: "#E2E8F0",
    border: "none",
    borderRadius: "10px",
    color: "#475569",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 800,
  },
};

export default RolFormModal;
